import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Grid2,
  Checkbox,
  FormControlLabel,
  Stack,
} from "@mui/material";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import AccountService from "../../../../services/AccountServices";
import { format, subMonths } from "date-fns";

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const sumOutstanding = (list) => {
  if (!Array.isArray(list) || !list.length) return 0;
  return list.reduce((total, row) => {
    const raw = row?.outstanding;
    if (raw === null || raw === undefined || raw === "" || raw === "-") return total;
    const num = Number(String(raw).replace(/,/g, ""));
    return total + (Number.isNaN(num) ? 0 : num);
  }, 0);
};

const displayStatus = (status) => {
  const raw = String(status || "").trim();
  if (!raw) return "-";
  if (raw.toLowerCase() === "full") return "Paid";
  return raw;
};

const CustomerLedgerEnquiryPopup = ({ open, onClose, customer }) => {
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");
  const authToken = token || accessToken;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [onlyOutstanding, setOnlyOutstanding] = useState(true);

  const customerId = customer?.id || "";
  const accountNo = String(
    customer?.accound_no ||
      customer?.account_no ||
      customer?.accountNo ||
      customer?.account_number ||
      ""
  ).trim();
  const customerName =
    customer?.company ||
    customer?.name ||
    customer?.customer_name ||
    "";

  const displayName = summary?.name || customerName || accountNo || "Customer";

  useEffect(() => {
    if (!open || !customerId) {
      setRows([]);
      setSummary(null);
      return;
    }
    let active = true;
    setLoading(true);
    const endDate = format(new Date(), "yyyy-MM-dd");
    const startDate = format(subMonths(new Date(), 2), "yyyy-MM-dd");
    AccountService.fetchSageCustomerTransactions(
      {
        company_id: customerId,
        customer_id: customerId,
        start_date: startDate,
        end_date: endDate,
        page: 1,
        per_page: 50,
      },
      authToken
    )
      .then((payload) => {
        if (!active) return;
        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.transactions)
            ? payload.transactions
            : Array.isArray(payload)
              ? payload
              : [];
        setRows(list);
        const source = payload?.customer || payload?.customer_details || list[0] || {};
        setSummary({
          name:
            source.customer_name ||
            source.name ||
            source.company_name ||
            customerName,
          reference: source.customer_reference || source.account_no || accountNo,
          short_name: source.short_name || source.shortname || "-",
          credit_limit: source.credit_limit ?? "-",
          balance: sumOutstanding(list),
          available_credit: source.available_credit ?? "-",
        });
      })
      .catch(() => {
        if (!active) return;
        setRows([]);
        setSummary(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, customerId, authToken, customerName, accountNo]);

  const filteredRows = useMemo(() => {
    if (!onlyOutstanding) return rows;
    return rows.filter((row) => {
      const raw = row?.outstanding;
      if (raw === null || raw === undefined || raw === "" || raw === "-") return false;
      const num = Number(String(raw).replace(/,/g, ""));
      return !Number.isNaN(num) && Math.abs(num) >= 0.0001;
    });
  }, [rows, onlyOutstanding]);

  const statusChip = (status) => {
    const label = displayStatus(status);
    const lower = String(label).toLowerCase();
    let color = "default";
    if (lower.includes("paid") && !lower.includes("un")) color = "success";
    else if (lower.includes("partial")) color = "warning";
    else if (lower.includes("outstanding") || lower.includes("due")) color = "error";
    return (
      <Chip
        label={label}
        size="small"
        color={color === "default" ? undefined : color}
        sx={{
          fontWeight: 600,
          borderRadius: "12px",
          ...(color === "default"
            ? { bgcolor: "rgba(145,158,171,0.16)", color: "#212b36" }
            : {}),
        }}
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        Customer Transaction Enquiry for {displayName}
        <IconButton onClick={onClose} size="small">
          <Iconify icon="mdi:close" width={20} height={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {summary && (
          <Grid2 container spacing={2} sx={{ mb: 2 }}>
            <Grid2 size={{ xs: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Short name
              </Typography>
              <Typography variant="subtitle2">{summary.short_name || "-"}</Typography>
            </Grid2>
            <Grid2 size={{ xs: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Credit limit (£)
              </Typography>
              <Typography variant="subtitle2">{formatMoney(summary.credit_limit)}</Typography>
            </Grid2>
            <Grid2 size={{ xs: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Balance (£)
              </Typography>
              <Typography variant="subtitle2">{formatMoney(summary.balance)}</Typography>
            </Grid2>
            <Grid2 size={{ xs: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Available credit (£)
              </Typography>
              <Typography variant="subtitle2">{formatMoney(summary.available_credit)}</Typography>
            </Grid2>
          </Grid2>
        )}
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={onlyOutstanding}
                onChange={(e) => setOnlyOutstanding(e.target.checked)}
              />
            }
            label="Only outstanding"
          />
        </Stack>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Trans. type</TableCell>
                <TableCell>Trans. date</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>2nd ref.</TableCell>
                <TableCell align="right">Value</TableCell>
                <TableCell align="right">Allocated</TableCell>
                <TableCell align="right">Outstanding</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>User</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      No transactions found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                filteredRows.map((row, index) => (
                  <TableRow
                    key={`${row.reference || "tx"}-${index}`}
                    hover
                    sx={{ "&:nth-of-type(even)": { bgcolor: "rgba(145,158,171,0.04)" } }}
                  >
                    <TableCell>{row.transaction_type_label || "-"}</TableCell>
                    <TableCell>{row.transaction_date_display || "-"}</TableCell>
                    <TableCell>{row.reference || "-"}</TableCell>
                    <TableCell>{row.second_reference || "-"}</TableCell>
                    <TableCell align="right">{formatMoney(row.value)}</TableCell>
                    <TableCell align="right">{formatMoney(row.allocated)}</TableCell>
                    <TableCell align="right">{formatMoney(row.outstanding)}</TableCell>
                    <TableCell>{statusChip(row.status)}</TableCell>
                    <TableCell>{row.user_name || "-"}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerLedgerEnquiryPopup;
